import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { getHolidayByDate, Holiday, isHoliday } from 'feiertagejs';
import { HippoConfirmationService } from '@hippo/services/confirmation';
import { lastValueFrom, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { UserService } from './user.service';
import { translate, TranslocoService } from '@jsverse/transloco';
import { environment } from 'environments/environment';
import * as Sentry from '@sentry/angular';
import { AuthService } from '../auth/auth.service';
import { Router } from '@angular/router';
import { AutoLogoutService } from './auto-logout.service';

@Injectable({
    providedIn: 'root',
})
export class SharedService {
    apiSystemLanguage: string;
    dataNotSavedActive = false;
    dataNotLoadedActive = false;

    isTouchDevice: boolean = true;
    pictureChanged = new Subject<void>();
    goToMyDataSettings = new Subject<void>();

    constructor(
        private hippoConfirmationService: HippoConfirmationService,
        private httpClient: HttpClient,
        private userService: UserService,
        private translocoService: TranslocoService,
        private authService: AuthService,
        private router: Router,
        private autoLogoutService: AutoLogoutService
    ) {}

    // Returns the ISO week of the date.
    getWeekNumber(date: Date) {
        return DateTime.fromJSDate(date).weekNumber;
    }

    getGermanHoliday(friday: Date): string {
        const saturday = new Date(friday);
        saturday.setDate(friday.getDate() + 1);
        const sunday = new Date(saturday);
        sunday.setDate(saturday.getDate() + 1);

        if (isHoliday(friday, 'ALL')) {
            const ft = <Holiday>getHolidayByDate(friday, 'ALL');
            switch (ft.name) {
                case 'HEILIGEDREIKOENIGE':
                    return ' (Drei Könige)';
                case 'NEUJAHRSTAG':
                    return ' (Neujahr)';
                case 'KARFREITAG':
                    return ' (Ostern)';
                case 'OSTERSONNTAG':
                    return ' (Ostern)';
                case 'OSTERMONTAG':
                    return ' (Ostern)';
                case 'TAG_DER_ARBEIT':
                    return ' (Maifeiertag)';
                case 'CHRISTIHIMMELFAHRT':
                    return ' (Christi Himmelfahrt)';
                case 'PFINGSTSONNTAG':
                    return ' (Pfingsten)';
                case 'PFINGSTMONTAG':
                    return ' (Pfingsten)';
                case 'DEUTSCHEEINHEIT':
                    return ' (Nationalfeiertag)';
                case 'ERSTERWEIHNACHTSFEIERTAG':
                    return ' (Weihnachtsfeiertage)';
                case 'ZWEITERWEIHNACHTSFEIERTAG':
                    return ' (Weihnachtsfeiertage)';
            }
        }
        if (isHoliday(saturday, 'ALL')) {
            const ft = <Holiday>getHolidayByDate(saturday, 'ALL');
            switch (ft.name) {
                case 'HEILIGEDREIKOENIGE':
                    return ' (Drei Könige)';
                case 'NEUJAHRSTAG':
                    return ' (Neujahr)';
                case 'KARFREITAG':
                    return ' (Ostern)';
                case 'OSTERSONNTAG':
                    return ' (Ostern)';
                case 'OSTERMONTAG':
                    return ' (Ostern)';
                case 'TAG_DER_ARBEIT':
                    return ' (Maifeiertag)';
                case 'CHRISTIHIMMELFAHRT':
                    return ' (Christi Himmelfahrt)';
                case 'PFINGSTMONTAG':
                    return ' (Pfingsten)';
                case 'DEUTSCHEEINHEIT':
                    return ' (Nationalfeiertag)';
                case 'ERSTERWEIHNACHTSFEIERTAG':
                    return ' (Weihnachtsfeiertage)';
                case 'ZWEITERWEIHNACHTSFEIERTAG':
                    return ' (Weihnachtsfeiertage)';
            }
        }
        if (isHoliday(sunday, 'ALL')) {
            const ft = <Holiday>getHolidayByDate(sunday, 'ALL');
            switch (ft.name) {
                case 'HEILIGEDREIKOENIGE':
                    return ' (Drei Könige)';
                case 'NEUJAHRSTAG':
                    return ' (Neujahr)';
                case 'KARFREITAG':
                    return ' (Ostern)';
                case 'OSTERSONNTAG':
                    return ' (Ostern)';
                case 'OSTERMONTAG':
                    return ' (Ostern)';
                case 'TAG_DER_ARBEIT':
                    return ' (Maifeiertag)';
                case 'CHRISTIHIMMELFAHRT':
                    return ' (Christi Himmelfahrt)';
                case 'PFINGSTMONTAG':
                    return ' (Pfingsten)';
                case 'DEUTSCHEEINHEIT':
                    return ' (Nationalfeiertag)';
                case 'ERSTERWEIHNACHTSFEIERTAG':
                    return ' (Weihnachtsfeiertage)';
                case 'ZWEITERWEIHNACHTSFEIERTAG':
                    return ' (Weihnachtsfeiertage)';
            }
        }
        if (friday.getMonth() === 11 && friday.getDate() === 24) {
            return ' (Weihnachten)';
        }
        if (friday.getMonth() === 11 && friday.getDate() === 31) {
            return ' (Silvester)';
        }
        if (saturday.getMonth() === 11 && saturday.getDate() === 24) {
            return ' (Weihnachten)';
        }
        if (saturday.getMonth() === 11 && saturday.getDate() === 31) {
            return ' (Silvester)';
        }
        if (sunday.getMonth() === 11 && sunday.getDate() === 24) {
            return ' (Weihnachten)';
        }
        if (sunday.getMonth() === 11 && sunday.getDate() === 31) {
            return ' (Silvester)';
        }

        return '';
    }

    convertToDate(dateString: string): Date | null {
        if (dateString !== null && dateString !== '') {
            let d: string[];
            let date: Date | null;
            if (dateString.includes('-')) {
                d = dateString.split('-');
                date = this.toUtcDate(
                    new Date(
                        (d[0] ?? '') + '/' + (d[1] ?? '') + '/' + (d[2] ?? '')
                    )
                );
            } else if (dateString.includes('.')) {
                d = dateString.split('.');
                date = this.toUtcDate(
                    new Date(
                        (d[2] ?? '') + '/' + (d[1] ?? '') + '/' + (d[0] ?? '')
                    )
                );
            } else {
                d = dateString.split('/');
                date = this.toUtcDate(
                    new Date(
                        (d[2] ?? '') + '/' + (d[1] ?? '') + '/' + (d[0] ?? '')
                    )
                );
            }
            return date;
        } else {
            return null;
        }
    }

    showAutoLogoutHint() {
        const message =
            this.translocoService.getActiveLang() === 'de'
                ? 'Ihre Sitzung ist abgelaufen. Sie wurden ausgeloggt. Bitte loggen Sie sich wieder ein.'
                : 'Your session has expired. You have been logged out. Please log in again.';
        const confirmation = this.hippoConfirmationService.open({
            title: 'Information',
            message,
            icon: { show: true, color: 'info' },
            actions: {
                confirm: {
                    label: 'OK',
                    color: 'primary',
                },
                cancel: {
                    show: false,
                },
            },
            dismissible: false,
        });
    }

    signOut(): void {
        this.showAutoLogoutHint();
        this.authService.signOut().subscribe();
        this.autoLogoutService.clearTimer();
        this.router.navigate(['/sign-in']);
    }

    showDataNotLoaded(
        error: any = null,
        component: string | null = null,
        functionName: string | null = null
    ) {
        if (
            error?.name === 'AbortError' ||
            error?.status === 0 ||
            error?.statusText === 'Unknown Error'
        ) {
            return;
        }

        const result = error?.error?.result;

        if (
            result === 'session expired' ||
            result?.message === 'session expired'
        ) {
            this.signOut();
        } else {
            if (environment.production) {
                let userId = this.userService.user.id;
                let userInitials = this.userService.user.login;
                this.sendErrorMail(error, component, functionName);
                Sentry.captureException(error, {
                    extra: {
                        component,
                        functionName,
                        userId,
                        userInitials,
                        errorObject: JSON.stringify(error, null, 2),
                    },
                });
            }
            if (this.dataNotLoadedActive) {
                return;
            }
            this.dataNotLoadedActive = true;
            const date = new Date().toLocaleString();
            let message =
                this.translocoService.getActiveLang() === 'de'
                    ? 'Es ist ein Fehler aufgetreten. Die Daten konnten nicht geladen werden. Prüfen Sie die Internetverbindung. Bei weiteren Problemen wenden Sie sich an ihren Systemadministrator!'
                    : 'An error has occurred. The data could not be loaded. Check your internet connection. If you have further problems, contact your system administrator!';
            message += `<br><br><span class="opacity-50">Error log(${date})</span><br><span class="opacity-50">C: ${component}</span><br><span class="opacity-50">F: ${functionName}</span><br><span class="opacity-50">TestSystem: ${environment.isTestSystem}</span>`;
            const confirmation = this.hippoConfirmationService.open({
                title: 'Information',
                message,
                icon: { show: true, color: 'error' },
                actions: {
                    confirm: {
                        label: 'OK',
                        color: 'primary',
                    },
                    cancel: {
                        show: false,
                    },
                },
                dismissible: false,
            });
            confirmation.afterClosed().subscribe(() => {
                this.dataNotLoadedActive = false;
            });
        }
    }

    showDataNotSaved(
        error: any = null,
        component: string | null = null,
        functionName: string | null = null
    ) {
        const result = error?.error?.result;

        if (
            result === 'session expired' ||
            result?.message === 'session expired'
        ) {
            this.signOut();
        } else {
            if (environment.production) {
                let userId = this.userService.user.id;
                let userInitials = this.userService.user.login;
                this.sendErrorMail(error, component, functionName);
                Sentry.captureException(error, {
                    extra: {
                        component,
                        functionName,
                        userId,
                        userInitials,
                        errorObject: JSON.stringify(error, null, 2),
                    },
                });
            }
            if (this.dataNotSavedActive) {
                return;
            }
            this.dataNotSavedActive = true;
            const date = new Date().toLocaleString();
            let message =
                this.translocoService.getActiveLang() === 'de'
                    ? 'Es ist ein Fehler aufgetreten. Die Daten konnten nicht gespeichert werden. Prüfen Sie die Internetverbindung. Bei weiteren Problemen wenden Sie sich an ihren Systemadministrator!'
                    : 'An error has occurred. The data could not be saved. Check your internet connection. If you have further problems, contact your system administrator!';
            message += `<br><br><span class="opacity-50">Error log(${date})</span><br><span class="opacity-50">C: ${component}</span><br><span class="opacity-50">F: ${functionName}</span><br><span class="opacity-50">TestSystem: ${environment.isTestSystem}</span>`;
            const confirmation = this.hippoConfirmationService.open({
                title: 'Information',
                message,
                icon: { show: true, color: 'error' },
                actions: {
                    confirm: {
                        label: 'OK',
                        color: 'primary',
                    },
                    cancel: {
                        show: false,
                    },
                },
                dismissible: false,
            });
            confirmation.afterClosed().subscribe(() => {
                this.dataNotSavedActive = false;
            });
        }
    }

    async sendErrorMail(
        error: any,
        component: string | null,
        functionName: string | null
    ) {
        if (environment.production) {
            const userId = this.userService.user.id ?? '';
            const userInitials = this.userService.user.login ?? '';
            const errorDetails = this.formatErrorDetails(
                error,
                component,
                functionName,
                userId,
                userInitials
            );

            try {
                await lastValueFrom(
                    this.httpClient.post<any>(
                        'https://hippo-tracking.codext.de/',
                        {
                            error: errorDetails,
                        }
                    )
                );
            } catch (error) {
                console.log(error);
            }
        }
    }

    showWrongFilledFields(fields: string[]) {
        let message = translate('common.save_error_mandatory') + ':';
        fields.forEach(field => {
            message += `</br>- ${field}`;
        });
        const confirmation = this.hippoConfirmationService.open({
            title: translate('common.warning'),
            message,
            actions: {
                confirm: {
                    label: 'OK',
                    color: 'primary',
                },
                cancel: {
                    show: false,
                },
            },
            dismissible: false,
        });
    }

    validateEmail(emailAddress) {
        let atSymbol = emailAddress.indexOf('@');
        let dotSymbol = emailAddress.lastIndexOf('.');
        let spaceSymbol = emailAddress.indexOf(' ');

        if (
            atSymbol != -1 &&
            atSymbol != 0 &&
            dotSymbol != -1 &&
            dotSymbol != 0 &&
            dotSymbol > atSymbol + 1 &&
            emailAddress.length > dotSymbol + 1 &&
            spaceSymbol == -1
        ) {
            return true;
        } else {
            return false;
        }
    }

    async fetchTranslations(lang: string, module: string) {
        const formData = new FormData();
        formData.append('iso_code', lang);
        formData.append('module', module);
        try {
            const data = await lastValueFrom(
                this.httpClient.post(
                    `hippo/navigator.php?page=pages/system/System_translations.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );
            return data;
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    async fetchCommonData(lang: string, option: string) {
        const tln_id = lang === 'de' ? '15' : '11';
        const formData = new FormData();
        formData.append('tln_id', tln_id);
        formData.append('option', option);
        try {
            const data = await lastValueFrom(
                this.httpClient.post(
                    `hippo/ajax_navigator.php?page=pages/api/Ajax_common_lists.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );
            return data;
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    async fetchCommonUserLists(lang: string, option: string) {
        const tln_id = lang === 'de' ? '15' : '11';
        const formData = new FormData();
        formData.append('tln_id', tln_id);
        formData.append('option', option);
        try {
            const data = await lastValueFrom(
                this.httpClient.post(
                    `hippo/ajax_navigator.php?page=pages/api/Ajax_user_lists.php&uid=${this.userService.user.uid}&page_type=json`,
                    formData,
                    {
                        observe: 'response',
                    }
                )
            );
            return data;
        } catch (error) {
            console.log(error);
            return error;
        }
    }

    showDataSuccessfulSaved() {
        this.hippoConfirmationService.open({
            title: 'Information',
            message: translate('common.save_success'),
            icon: { show: true, color: 'info', name: 'info' },
            actions: {
                confirm: {
                    label: 'OK',
                    color: 'primary',
                },
                cancel: {
                    show: false,
                },
            },
            dismissible: false,
        });
    }

    // get from a string the string and the string without accents
    // example 'Lázár'=>['Lázár','Lazar']
    getStringAndDeaccentedString(input: string) {
        if (!input) {
            input = '';
        }
        let output: string[] = [];
        output.push(input);
        output.push(input.normalize('NFD').replace(/\p{Diacritic}/gu, ''));
        return output;
    }

    checkTouchDevice() {
        this.isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
    }

    isValidKey(key: string | null | undefined): boolean {
        return (
            key !== undefined &&
            key !== null &&
            key.trim() !== '' &&
            typeof key === 'string' &&
            !isNaN(+key) &&
            +key > 0
        );
    }

    formatErrorDetails(
        error: any,
        component: string | null,
        functionName: string | null,
        userId: string,
        userInitials: string
    ): string {
        let errorDetails: any = {};

        if (error instanceof Error) {
            errorDetails = {
                component,
                functionName,
                isTestSystem: environment.isTestSystem,
                userId,
                userInitials,
                message: error.message,
                stack: error.stack,
                name: error.name,
            };
        } else if (typeof error === 'object' && error !== null) {
            errorDetails = {
                component,
                functionName,
                isTestSystem: environment.isTestSystem,
                userId,
                userInitials,
                ...error,
            };
        } else {
            errorDetails = {
                component,
                functionName,
                isTestSystem: environment.isTestSystem,
                userId,
                userInitials,
                message: String(error),
            };
        }

        return JSON.stringify(errorDetails); // Beautify JSON output
    }

    checkNullBlank(value: any): boolean {
        return (
            value !== null &&
            value !== undefined &&
            value.toString().trim() !== ''
        );
    }

    checkInteger(value: string | null): boolean {
        if (value === null) return false;
        return /^-?\d+$/.test(value);
    }

    isValidIndex(key: string): boolean {
        return (
            key !== null &&
            key !== undefined &&
            key.trim() !== '' &&
            typeof key === 'string' &&
            !isNaN(+key) &&
            Number.isInteger(+key) &&
            +key >= 0
        );
    }

    toUtcDate(d: Date | null | undefined): Date | null {
        const MS_PER_DAY = 24 * 60 * 60 * 1000;
        if (!d || isNaN(d.getTime())) return null;

        // robustes Modulo auch für Zeiten < 1970
        const t = d.getTime();
        const r = ((t % MS_PER_DAY) + MS_PER_DAY) % MS_PER_DAY;

        const isUtcMidnight = r === 0; // exakt 00:00:00.000Z

        // Fall A: stammt aus "YYYY-MM-DD" (UTC-Mitternacht) → UTC-Komponenten nehmen
        if (isUtcMidnight) {
            return new Date(
                d.getUTCFullYear(),
                d.getUTCMonth(),
                d.getUTCDate()
            );
        }

        // Fall B: echte lokale Zeit (z. B. "1974-11-09 00:00:00" als lokal geparst)
        // → lokale Komponenten nehmen
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    getBerlinTimestamp(d: Date | null | undefined): number {
        if (!d || isNaN(d.getTime())) return NaN;
        // Jahr
        let year2 = d.getFullYear().toString();

        // Monat mit führender Null, falls < 10
        let month2 = (d.getMonth() + 1).toString().padStart(2, '0');

        // Tag mit führender Null, falls < 10
        let day2 = d.getDate().toString().padStart(2, '0');

        // Zusammenfügen
        let isoDateString = `${year2}-${month2}-${day2}`;
        // Datum als UTC interpretieren
        const d2 = new Date(isoDateString + 'T00:00:00Z');

        // Berlin-Komponenten extrahieren
        const fmt = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Europe/Berlin',
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });

        const berlinString = fmt.format(d2).replace(', ', 'T'); // "YYYY-MM-DDTHH:mm:ss"
        const [datePart, timePart] = berlinString.split('T');
        if (!datePart || !timePart) {
            return Date.UTC(1970, 0, 1);
        }
        const dateParts = datePart.split('-').map(Number);
        const timeParts = timePart.split(':').map(Number);
        const year = dateParts[0] ?? 1970;
        const month = dateParts[1] ?? 1;
        const day = dateParts[2] ?? 1;
        const hour = timeParts[0] ?? 0;
        const minute = timeParts[1] ?? 0;
        const second = timeParts[2] ?? 0;

        const isSummerTime = (d: Date) =>
            d.getTimezoneOffset() <
            new Date(d.getFullYear(), 0, 1).getTimezoneOffset();

        return (
            Date.UTC(year, month - 1, day, hour, minute, second) -
            (isSummerTime(d2) ? 14400000 : 7200000)
        );
    }

    // toUtcDateFromTimestamp(d: Date | null): Date | null {
    //     // if (!d) return null;
    //     return d;

    //     // Wenn eine Zahl übergeben wird, als Timestamp behandeln
    //     const date = typeof d === 'number' ? new Date(d) : d;
    //     if (isNaN(date.getTime())) return null;

    //     // Alle Komponenten aus UTC holen und in einem "local-like" Date zurückgeben
    //     return new Date(
    //         date.getUTCFullYear(),
    //         date.getUTCMonth(),
    //         date.getUTCDate(),
    //         date.getUTCHours(),
    //         date.getUTCMinutes(),
    //         date.getUTCSeconds(),
    //         date.getUTCMilliseconds()
    //     );
    // }

    toUtcDateFromTimestamp(input: number | Date): Date {
        const date = input instanceof Date ? input : new Date(input);

        // Zeit in Europe/Berlin ermitteln
        const fmt = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Europe/Berlin',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });

        const parts = fmt.formatToParts(date);
        const y = Number(parts.find(p => p.type === 'year')?.value);
        const m = Number(parts.find(p => p.type === 'month')?.value);
        const d = Number(parts.find(p => p.type === 'day')?.value);
        const h = Number(parts.find(p => p.type === 'hour')?.value);
        const min = Number(parts.find(p => p.type === 'minute')?.value);
        const s = Number(parts.find(p => p.type === 'second')?.value);

        // Erzeuge ein Date in *lokaler Zeit*, damit Uhrzeit fix bleibt
        return new Date(y, m - 1, d, h, min, s);
    }

    toUtcDateFromDateStringWithTimeT(
        value: string | Date | number | null | undefined
    ): Date | null {
        if (!value) return null;

        const date =
            typeof value === 'string' || typeof value === 'number'
                ? new Date(value)
                : value;
        if (isNaN(date.getTime())) return null;

        // Nutze UTC-Komponenten, um ein "festes" Date zu erzeugen
        return new Date(
            Date.UTC(
                date.getUTCFullYear(),
                date.getUTCMonth(),
                date.getUTCDate(),
                date.getUTCHours(),
                date.getUTCMinutes(),
                date.getUTCSeconds(),
                date.getUTCMilliseconds()
            )
        );
    }

    showWrongDateAltert() {
        const confirmation = this.hippoConfirmationService.open({
            title: translate('common.error'),
            message: `<span class="text-red-500">${translate('codext.wrongDate')}</span>`,
            icon: { show: false },
            actions: {
                confirm: {
                    label: 'OK',
                    color: 'primary',
                },
                cancel: {
                    show: false,
                },
            },
            dismissible: false,
        });
    }
}
